/**
 * batch-62 klant-vaste-inzet (FO-458) - DE pure beslissing "hoort deze monteur
 * bij de vaste inzet van deze klant?".
 *
 * DB-vrij (zelfde opzet als `bezettingsstatus.ts`): de API-loader
 * (`apps/api/src/services/vaste-inzet-kring.ts`) verzamelt per kandidaat de
 * feiten en deze module oordeelt. Afwezigheidspagina, jaarplanning-teller,
 * weekplanning-afwezigheid, monteurspagina en het beheer op de relatie-tab
 * lezen allemaal DEZE uitkomst; daarom kunnen hun aantallen niet uiteenlopen.
 *
 * De kring (spec 158 §3.1), in deze volgorde:
 *   a. vaste monteur: een actieve FO-440-koppeling (`WerkbonVasteMonteur`),
 *      bevestigd óf voorlopig-afgeleid;
 *   b. lopend ingepland: de GEPLANDE uren in het venster halen op zichzelf de
 *      Regelmatig-grens van de klant ÉN de geplande diensten liggen verder uit
 *      elkaar dan één week. Bewust geen "staat ergens ingepland": dan viel elke
 *      ingeplande invaller erin en bestond de uitzondering van §3.2 (incidentele
 *      uitval op de dienst, niet als naam) niet. De spreiding houdt ook de
 *      invaller van één week in een continurooster buiten (zeven 12-uurs-
 *      diensten = 84 uur, boven de grens, maar binnen één week);
 *   c. ervaring: de cumulatieve gewerkte uren (FO-413-D5) geven REGELMATIG of
 *      INGEWERKT, met de klant-eigen grenzen - maar alleen bij RECENTE inzet
 *      (een dagregel in de laatste 90 dagen of in het venster). De urencache
 *      vervalt nooit; zonder die voorwaarde bleef een oud-monteur die vorig jaar
 *      400 uur liep en nu elders werkt voor altijd "vaste inzet";
 *   minus een handmatige uitsluiting (wint altijd), plus een handmatige
 *   toevoeging (een vaste kracht die net begint en nog geen uren heeft).
 *
 * Strengste wint: wie voor deze klant niet zichtbaar is (globale vlag, per-klant
 * verborgen, vertrokken) komt nooit in de kring, ook niet via een toevoeging.
 */
import type {
  KlantAfwezigePeriode,
  KlantVasteInzetGrond,
  KlantVasteInzetRol,
  KlantVasteInzetStatus,
  KlantVasteInzetUitzondering,
} from '@streep/types'
import { schuifDag } from './bezettingsstatus'
import {
  bepaalErvaringCategorie,
  type ErvaringGrenzen,
  type KlantErvaringCategorieCode,
} from './klant-ervaring'

// ─── Drempels ────────────────────────────────────────────────────────────────

/**
 * Hoe ver terug een dagregel nog "recente inzet" is voor de ervaringsgrond
 * (c). Zelfde 90 dagen als de vroegere tab "Toegewezen" op de monteurspagina.
 */
export const VASTE_INZET_RECENT_DAGEN = 90

/**
 * Minimale afstand in kalenderdagen tussen de eerste en de laatste geplande
 * dag voor "lopend ingepland" (b): de planning moet verder reiken dan één week.
 */
export const LOPENDE_PLANNING_MIN_SPREIDING_DAGEN = 8

// ─── Kring-oordeel per kandidaat ─────────────────────────────────────────────

export interface VasteInzetKandidaat {
  employeeId: string
  /** Statussen van de actieve FO-440-koppelingen op diensten van deze klant. */
  vasteMonteurStatussen: ReadonlyArray<'BEVESTIGD' | 'VOORLOPIG'>
  /** Som van de geplande uren bij deze klant in het venster. */
  geplandeUren: number
  /** Kalenderdagen tussen de eerste en de laatste geplande dag in het venster (0 bij hooguit één dag). */
  geplandeSpreidingDagen: number
  /** Cumulatieve gewerkte uren bij deze klant (D5-cache); `null` zonder telling. */
  gewerkteUren: number | null
  /** Een klant-zichtbare dagregel bij deze klant in de laatste 90 dagen of in het venster. */
  recenteInzet: boolean
  uitzondering: KlantVasteInzetUitzondering | null
  /** Klant-zichtbaar en niet vertrokken. */
  zichtbaar: boolean
}

export interface VasteInzetBeoordeling {
  employeeId: string
  status: KlantVasteInzetStatus
  /** Alleen bij `IN_KRING`. */
  rol: KlantVasteInzetRol | null
  /** Automatische gronden plus `HANDMATIG` bij een toevoeging; ook bij uitsluiting gevuld (beheer-uitleg). */
  gronden: KlantVasteInzetGrond[]
  ervaring: KlantErvaringCategorieCode | null
}

/**
 * "Lopend ingepland": de geplande uren in het venster zouden op zichzelf al
 * Regelmatig zijn (de D5-grens van de klant, zodat een klant die zijn grenzen
 * verzet dat ook hier terugziet, spec §6.4) en de planning reikt verder dan één
 * week.
 */
export function isLopendIngepland(
  geplandeUren: number,
  geplandeSpreidingDagen: number,
  grenzen?: Partial<ErvaringGrenzen> | null,
): boolean {
  return (
    geplandeSpreidingDagen >= LOPENDE_PLANNING_MIN_SPREIDING_DAGEN &&
    bepaalErvaringCategorie(geplandeUren, grenzen) !== 'INCIDENTEEL'
  )
}

export function beoordeelVasteInzet(
  kandidaat: VasteInzetKandidaat,
  grenzen?: Partial<ErvaringGrenzen> | null,
): VasteInzetBeoordeling {
  const gronden: KlantVasteInzetGrond[] = []
  if (kandidaat.vasteMonteurStatussen.includes('BEVESTIGD')) {
    gronden.push('VASTE_MONTEUR')
  } else if (kandidaat.vasteMonteurStatussen.includes('VOORLOPIG')) {
    gronden.push('VASTE_MONTEUR_VOORLOPIG')
  }
  if (isLopendIngepland(kandidaat.geplandeUren, kandidaat.geplandeSpreidingDagen, grenzen)) {
    gronden.push('LOPENDE_PLANNING')
  }
  const ervaring =
    kandidaat.gewerkteUren === null ? null : bepaalErvaringCategorie(kandidaat.gewerkteUren, grenzen)
  if ((ervaring === 'REGELMATIG' || ervaring === 'INGEWERKT') && kandidaat.recenteInzet) {
    gronden.push('ERVARING')
  }
  if (kandidaat.uitzondering === 'TOEGEVOEGD') gronden.push('HANDMATIG')

  let status: KlantVasteInzetStatus
  if (kandidaat.uitzondering === 'UITGESLOTEN') status = 'UITGESLOTEN'
  else if (gronden.length === 0) status = 'BUITEN_KRING'
  else if (!kandidaat.zichtbaar) status = 'NIET_ZICHTBAAR'
  else status = 'IN_KRING'

  return {
    employeeId: kandidaat.employeeId,
    status,
    rol:
      status === 'IN_KRING'
        ? gronden.includes('VASTE_MONTEUR')
          ? 'VASTE_MONTEUR'
          : 'REGELMATIG'
        : null,
    gronden,
    ervaring,
  }
}

/**
 * De rol op een afwezigheidsmelding. Is de afwezige op (een van) de diensten de
 * bevestigde vaste monteur, dan leest de klant "vaste monteur" - ook als de
 * kring hem om een andere reden kent. Anders volgt de rol de kring.
 */
export function klantAfwezigheidRol(
  kringRol: KlantVasteInzetRol | null | undefined,
  isVasteMonteurOpDienst: boolean,
): KlantVasteInzetRol {
  return isVasteMonteurOpDienst || kringRol === 'VASTE_MONTEUR' ? 'VASTE_MONTEUR' : 'REGELMATIG'
}

// ─── Afwezige perioden (klant-weergave) ──────────────────────────────────────

export interface KlantAfwezigeDag {
  /** `yyyy-MM-dd` */
  datum: string
  vervangerNaam: string | null
  wordtIngepland: boolean
  vervangingViaPlanning: boolean
}

/**
 * Vouwt afwezige dagen samen tot perioden: opeenvolgende kalenderdagen met
 * dezelfde vervangingsstatus vormen één periode ("ma 14 t/m wo 16 sep, vervangen
 * door Piet"). Een dag zonder afwezigheid ertussen, of een andere vervanger,
 * begint een nieuwe periode - samenvoegen over zo'n dag zou een dag claimen
 * waarop de monteur er gewoon is. Een dubbele dag telt één keer.
 */
export function groepeerAfwezigePerioden(
  dagen: ReadonlyArray<KlantAfwezigeDag>,
): KlantAfwezigePeriode[] {
  const gesorteerd = [...dagen].sort((a, b) => a.datum.localeCompare(b.datum))
  const uit: KlantAfwezigePeriode[] = []
  for (const dag of gesorteerd) {
    const laatste = uit[uit.length - 1]
    if (laatste && laatste.tot === dag.datum) continue
    if (
      laatste &&
      schuifDag(laatste.tot, 1) === dag.datum &&
      laatste.vervangerNaam === dag.vervangerNaam &&
      laatste.wordtIngepland === dag.wordtIngepland &&
      laatste.vervangingViaPlanning === dag.vervangingViaPlanning
    ) {
      laatste.tot = dag.datum
      laatste.aantalDagen += 1
      continue
    }
    uit.push({
      van: dag.datum,
      tot: dag.datum,
      aantalDagen: 1,
      vervangerNaam: dag.vervangerNaam,
      wordtIngepland: dag.wordtIngepland,
      vervangingViaPlanning: dag.vervangingViaPlanning,
    })
  }
  return uit
}
